import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { usePinSession } from '@/lib/auth/pin/usePinSession';

interface Props {
  children: ReactNode;
  /** Where to send unauthenticated users. Defaults to /lock (the kiosk gate). */
  redirectTo?: string;
}

/**
 * Gate a route on authentication of either tier:
 *   - Tier-1: an MSAL-active account.
 *   - Tier-2: a non-expired PIN session in sessionStorage.
 *
 * While MSAL is mid-redirect we render nothing so the redirect can complete
 * without flashing. If the active session must change its PIN, we forward to
 * /pin-change instead of the requested route.
 */
export function RequireAuth({ children, redirectTo = '/lock' }: Props): ReactNode {
  const isMsalAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const { session: pinSession } = usePinSession();
  const location = useLocation();

  if (inProgress !== InteractionStatus.None) {
    return null;
  }
  const authed = isMsalAuthenticated || pinSession !== null;
  if (!authed) {
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }
  if (pinSession?.mustChangePin && location.pathname !== '/pin-change') {
    return <Navigate to="/pin-change" replace />;
  }
  return <>{children}</>;
}
